import { uniq, pick, groupBy, keyBy, flatMap, fromPairs, mapValues, min, pickBy, max } from 'lodash';

import memoizeGetter from '../../../helpers/memoizeGetter';
import { scrollTop } from '../../../helpers/window_scroll';

export const STEP_MODES = {
  TABS: 'tabs',
  STEPS: 'steps',
  NONE: 'none',
};

const deprecatedWarning = (() => {
  let warned = false;
  return () => {
    if (!warned) {
      console.warn('[Deprecated] Using legacy multi-step syntax. Consider updating form definition to the new syntax.');
      warned = true;
    }
  };
})();

function checkEditMode() {
  return typeof window === 'object' && !!window.Board?.editable;
}

class MultiStepFormProps {
  constructor({
    uiSchema,
    resolvedSchema,
    currentStepIndex,
    setCurrentStepIndex,
    validate,
    validateShowErrors,
    clearErrors,
  }) {
    this.uiSchema = uiSchema || {};
    this.schema = resolvedSchema || {};
    // we provide a default for activeStep index
    this.currentStepIndexFromState = currentStepIndex;
    this.setCurrentStepIndex = setCurrentStepIndex;
    this.validate = validate;
    this.validateShowErrors = validateShowErrors;
    this.clearErrors = clearErrors;
  }

  get meta() {
    return this.schema.meta || {};
  }

  get properties() {
    return this.schema.properties || {};
  }

  /**
   * steps, as defined in UI schema
   * or, in the legacy case, schema.meta.tabs
   * [
   *   { title: 'Step 1', id: 1, index: 0 }
   * ]
   * blank steps are NOT filtered out for this method
   * "id" and "stepNumber" are the same thing
   */
  get steps() {
    const stepsFromUI = this.uiSchema['ui:options']?.steps || [];
    const stepsByNumber = keyBy(stepsFromUI, 'step');
    Object.entries(this.properties).forEach(([_propKey, { step, title: propTitle }]) => {
      if (step && !stepsByNumber[step]) {
        // we've discovered a step that from the schema alone (not mentioned in UI schema)
        // name it by the property's title or a generic fallback
        const title = propTitle || `Step ${step}`;
        stepsByNumber[step] = { step, title };
      }
    });
    const steps = Object.values(stepsByNumber).sort((a, b) => a.step - b.step);
    if (steps.length > 0) {
      return steps.map(({ title, step: id }, index) => ({ title, id, index }));
    }

    if (this.isLegacyMultiStep) {
      // convert the old syntax to the new
      // rename "label", drop "fields", add "id"
      return this.stepsOLD.map(({ label }, index) => ({ title: label, id: index + 1, index }));
    }

    return null;
  }

  /**
   * legacy steps defined in schema meta.tabs
   * [
   *   { label: 'Step 1', fields: ['email', 'name']}
   * ]
   */
  get stepsOLD() {
    return this.meta?.tabs;
  }

  get hasAnySteps() {
    const { steps } = this;
    return Array.isArray(steps) && steps.length > 0;
  }

  get stepMode() {
    const { hasAnySteps, uiSchema } = this;
    if (hasAnySteps) {
      const stepMode = uiSchema['ui:options']?.stepMode;
      if (stepMode) return stepMode;

      const { stepModeOLD } = this;
      if (stepModeOLD) return stepModeOLD;

      return STEP_MODES.TABS;
    }
    return STEP_MODES.NONE;
  }

  get stepModeOLD() {
    return this.meta?.stepMode;
  }

  get isLegacyMultiStep() {
    if (this.stepsOLD) {
      deprecatedWarning();
      return true;
    }
    return false;
  }

  get isTabbed() {
    const { hasAnySteps, stepMode } = this;
    return hasAnySteps && stepMode === STEP_MODES.TABS;
  }

  get isMultiStep() {
    const { hasAnySteps, stepMode, presentStepNumbers } = this;
    return stepMode !== STEP_MODES.NONE && hasAnySteps && presentStepNumbers.length > 0;
  }

  get currentStepIndex() {
    if (!this.isMultiStep) return null;

    const { currentStepIndexFromState } = this;
    return Number.isInteger(currentStepIndexFromState) ? currentStepIndexFromState : this.getFirstPresentStepIndex();
  }

  get currentStep() {
    if (!this.isMultiStep) return null;

    const { steps, currentStepIndex } = this;
    return steps[currentStepIndex];
  }

  get currentStepNumber() {
    if (!this.isMultiStep) return null;

    return this.currentStep?.id;
  }

  get stepsByNumber() {
    return keyBy(this.steps, 'id');
  }

  get isLastStep() {
    if (!this.isMultiStep) return null;

    const { stepsByNumber, presentStepNumbers, currentStepIndex } = this;
    const lastStep = stepsByNumber[max(presentStepNumbers)];
    return currentStepIndex === lastStep.index;
  }

  get isFirstStep() {
    if (!this.isMultiStep) return null;

    const { stepsByNumber, presentStepNumbers, currentStepIndex } = this;
    const firstStep = stepsByNumber[min(presentStepNumbers)];
    return currentStepIndex === firstStep.index;
  }

  /**
   * we need a mapping of fields => stepNumbers for various lookups
   * { email: 1, name: 1, address: 2 }
   */
  get stepNumbersByField() {
    if (this.isLegacyMultiStep) {
      return this.stepNumbersByFieldOLD;
    }

    const { properties } = this;
    return mapValues(properties, 'step');
  }

  get stepNumbersByFieldOLD() {
    const { stepsOLD, steps, properties } = this;

    // old steps sytanx gives an array of fields that it selects [{ fields: ['f1', 'f2'] }, ...]
    // flatmap them with the step numbers to pairs [['f1', 1], ['f2', 1], ...]
    const stepNumbersByFieldOLD = fromPairs(
      flatMap(stepsOLD, ({ fields }, index) => {
        const stepNumber = steps[index]?.id;
        return (fields || []).map(field => [field, stepNumber]);
      })
    );
    // we only want the fields which are actually in the resolved schema
    // the meta may point to conditional fields hidden or otherwise just invalid `tabs` definition
    // grab the intersection with `properties`, default `undefined` for fields with no step defined
    return mapValues(properties, (_, field) => stepNumbersByFieldOLD[field] || undefined);
  }

  /** [1, 2] */
  get presentStepNumbers() {
    return uniq(Object.values(this.stepNumbersByField)).filter(Boolean);
  }

  /**
   * `steps`, but with a computed boolean `present` property on each
   * "present" step means the currently resolved schema has at least one field
   */
  get stepsWithPresence() {
    if (!this.isMultiStep) return null;

    const { steps, presentStepNumbers } = this;
    return steps.map(step => ({
      ...step,
      present: presentStepNumbers.includes(step.id),
    }));
  }

  getFirstPresentStepIndex(start = 0) {
    const { stepsWithPresence } = this;
    for (let i = start; i < this.steps.length; i += 1) {
      if (stepsWithPresence[i]?.present) {
        return i;
      }
    }
    return -1;
  }

  get fieldsWithNoStep() {
    if (!checkEditMode()) return null;
    if (!this.isMultiStep) return null;

    // this indicates a form misconfiguration, we'll want to warn about it in edit mode
    // case: we have a root level field, were in stepped mode, but it doesn't belong to a step
    const { stepsByNumber, stepNumbersByField } = this;
    return pickBy(stepNumbersByField, stepNumber => !(stepNumber in stepsByNumber));
  }

  /** callback to be used in field templates to indicate issues with step definition */
  getIssueForField = ({ id: fieldPath }) => {
    if (!checkEditMode()) return null;
    if (!this.isMultiStep) return null;

    const field = fieldPath?.replace(/^root_/, '');
    const { fieldsWithNoStep } = this;
    if (field in fieldsWithNoStep) {
      const stepNumber = fieldsWithNoStep[field];
      return `${field} has no corresponding step: ${JSON.stringify([stepNumber])}`;
    }
    return null;
  };

  /**
   * this callback should be used in the field templates to determine if a
   * field hides due to being outside the current step
   */
  shouldHideForStep = ({ id: fieldPath, schema }) => {
    if (!this.isMultiStep) return false;

    let stepNumber = schema?.step;
    if (!stepNumber) {
      // must be legacy (or a mistake) if step is not in the field schema
      // in that case, we can look it up by the field name
      const field = fieldPath?.replace(/^root_/, '');
      stepNumber = this.stepNumbersByField[field];
    }
    if (!stepNumber) return false;
    if (this.currentStepNumber === stepNumber) return false;
    if (!(stepNumber in this.stepsByNumber)) return false; // error state, step doesn't exist

    return true;
  };

  get firstStepHavingError() {
    const { errors } = this.validate();
    // this regex grabs the first part of the path, they're dot separated
    // so ".address.street" => "address"
    const rootErrorFields = uniq(errors.map(({ property }) => property.replace(/^\.?([^.]+).*$/, '$1')));
    const { stepNumbersByField } = this;
    const errorFieldsByStepNumber = groupBy(rootErrorFields, field => stepNumbersByField[field]);
    const errorStepNumbers = Object.keys(errorFieldsByStepNumber).map(Number).filter(Boolean);
    if (errorStepNumbers.length === 0) {
      return null;
    }
    return this.stepsByNumber[min(errorStepNumbers)];
  }

  /** callback for changing to an arbitrary step */
  handleStepSelect = nextIndex => {
    if (!this.isMultiStep) return;

    const { setCurrentStepIndex, currentStepIndex } = this;
    if (nextIndex === currentStepIndex) return;

    if (nextIndex < currentStepIndex) {
      // we can back up without validating
      scrollTop();
      setCurrentStepIndex(nextIndex);
      return;
    }

    const { firstStepHavingError } = this;
    const firstErrorIndex = firstStepHavingError?.index;
    if (firstErrorIndex !== null && firstErrorIndex < nextIndex) {
      this.validateShowErrors(); // validation failed, show errors
      if (firstErrorIndex > currentStepIndex) {
        // we're trying to go to step 3 from step 1, but we just found errors on step 2, go there instead
        setCurrentStepIndex(firstErrorIndex);
      }
      return;
    }
    // step advance passed, we don't want live validation on when they first reach a new step
    scrollTop();
    this.clearErrors();
    setCurrentStepIndex(nextIndex);
  };

  /** callback for next step buttons */
  handleNextStep = () => {
    if (!this.isMultiStep) return;
    if (this.isLastStep) return;

    const nextStepIndex = this.getFirstPresentStepIndex(this.currentStepIndex + 1);
    this.handleStepSelect(nextStepIndex);
  };

  handlePreviousStep = () => {
    if (!this.isMultiStep) return;
    if (this.isFirstStep) return;

    const { stepsWithPresence } = this;
    let i = this.currentStepIndex - 1;
    for (; i >= 0; i -= 1) {
      if (stepsWithPresence[i]?.present) {
        break;
      }
    }

    this.handleStepSelect(i);
  };
}

const getters = [
  'meta',
  'properties',
  'steps',
  'stepsOLD',
  'hasAnySteps',
  'stepMode',
  'stepModeOLD',
  'isLegacyMultiStep',
  'isTabbed',
  'isMultiStep',
  'currentStepIndex',
  'currentStep',
  'currentStepNumber',
  'stepsByNumber',
  'isLastStep',
  'isFirstStep',
  'stepNumbersByField',
  'stepNumbersByFieldOLD',
  'presentStepNumbers',
  'stepsWithPresence',
  'fieldsWithNoStep',
  'firstStepHavingError',
];

const callbacks = ['getIssueForField', 'shouldHideForStep', 'handleStepSelect', 'handleNextStep', 'handlePreviousStep'];

getters.forEach(fn => memoizeGetter(MultiStepFormProps, fn));

export default function createMultiStepFormProps({
  uiSchema,
  resolvedSchema,
  currentStepIndex,
  setCurrentStepIndex,
  validate,
  validateShowErrors,
  clearErrors,
}) {
  const multiStepFormProps = new MultiStepFormProps({
    uiSchema,
    resolvedSchema,
    currentStepIndex,
    setCurrentStepIndex,
    validate,
    validateShowErrors,
    clearErrors,
  });
  return {
    ...pick(multiStepFormProps, [
      'isLegacyMultiStep',
      'isMultiStep',
      'isTabbed',
      'isFirstStep',
      'isLastStep',
      'currentStepIndex',
      ...callbacks,
    ]),
    steps: multiStepFormProps.stepsWithPresence,
  };
}
